<template>
  <div class="ls-sidebar-wrapper" v-if="isLoggedIn() && isNotAdminManager()">
    <nav class="ls-drawer">
      <!-- Brand Section -->
      <router-link :to="{ name: 'Dashboard' }" class="ls-brand">
        <img src="@/assets/logo-on-dark.png" alt="CertifAI" class="ls-brand-logo" />
      </router-link>

      <div class="ls-divider"></div>

      <!-- Main Navigation -->
      <div class="ls-nav-list">
        <template v-for="(section, sIdx) in sections">
          <!-- Section divider + label (skip for the first section) -->
          <div v-if="sIdx > 0" :key="'div-' + sIdx" class="ls-section-divider">
            <div class="ls-section-line"></div>
            <span class="ls-section-label">{{ section.label }}</span>
            <div class="ls-section-line"></div>
          </div>

          <template v-for="item in section.items">
            <!-- Sub-menu group item -->
            <v-menu
              v-if="item.subItems"
              :key="item.text + '-group'"
              :offset-x="true"
              max-width="230px"
              transition="slide-x-transition"
            >
              <template v-slot:activator="{ on }">
                <div
                  v-on="on"
                  class="ls-nav-item"
                  :class="{ 'ls-nav-item--active': isSacGroupActive }"
                >
                  <div class="ls-icon-wrapper">
                    <v-icon size="24">{{ item.icon }}</v-icon>
                  </div>
                  <span class="ls-nav-label">{{ item.text }}</span>
                  <v-icon x-small class="ls-chevron-icon">mdi-chevron-right</v-icon>
                </div>
              </template>
              <v-list class="ls-overflow-menu" dense>
                <div class="ls-overflow-header">
                  <v-icon small color="white" class="mr-2">{{ item.icon }}</v-icon>
                  <span>{{ item.text }}</span>
                </div>
                <v-list-item
                  v-for="sub in item.subItems"
                  :key="sub.text"
                  class="ls-overflow-item"
                  link
                  :to="sub.to"
                >
                  <v-list-item-icon class="mr-3">
                    <v-icon size="20">{{ sub.icon }}</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title class="ls-overflow-label">{{ sub.text }}</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>

            <!-- Regular routed item -->
            <router-link
              v-else-if="item.to"
              :key="item.text"
              :to="item.to"
              custom
              v-slot="{ navigate, isActive }"
            >
              <div
                class="ls-nav-item"
                :class="{ 'ls-nav-item--active': isActive }"
                @click="navigate"
              >
                <div class="ls-icon-wrapper">
                  <v-icon size="24">{{ item.icon }}</v-icon>
                </div>
                <span class="ls-nav-label">{{ item.text }}</span>
              </div>
            </router-link>
            <a
              v-else-if="item.href"
              :key="item.text + '-link'"
              :href="item.href"
              :target="item.target"
              class="ls-nav-item"
            >
              <div class="ls-icon-wrapper">
                <v-icon size="24">{{ item.icon }}</v-icon>
              </div>
              <span class="ls-nav-label">{{ item.text }}</span>
            </a>
          </template>
        </template>

        <!-- More Tools -->
        <v-menu :offset-x="true" max-width="230px" transition="slide-x-transition" top>
          <template v-slot:activator="{ on }">
            <div
              v-on="on"
              class="ls-nav-item ls-nav-item--tools"
              :class="{ 'ls-nav-item--active': isMoreToolsActive }"
            >
              <div class="ls-icon-wrapper ls-icon-wrapper--tools">
                <v-icon size="24">mdi-tools</v-icon>
              </div>
              <span class="ls-nav-label">{{ $t('moreOptionsButton') }}</span>
              <v-icon x-small class="ls-chevron-icon">mdi-chevron-right</v-icon>
            </div>
          </template>
          <v-list class="ls-overflow-menu" dense>
            <div class="ls-overflow-header">
              <v-icon small color="white" class="mr-2">mdi-apps</v-icon>
              <span>More Tools</span>
            </div>
            <template v-for="menuItem in menuItems">
              <v-list-item
                :key="menuItem.title"
                class="ls-overflow-item"
                link
                v-bind="
                  menuItem.to
                    ? { to: menuItem.to }
                    : { href: menuItem.href, target: menuItem.target, rel: menuItem.rel }
                "
              >
                <v-list-item-icon class="mr-3">
                  <v-icon size="20">{{ menuItem.icon }}</v-icon>
                </v-list-item-icon>
                <v-list-item-title class="ls-overflow-label">{{
                  menuItem.title
                }}</v-list-item-title>
              </v-list-item>
            </template>
          </v-list>
        </v-menu>
      </div>
    </nav>
  </div>
</template>

<script>
import { defineComponent } from '@vue/composition-api';
import util from '@/mixins/util.js';

export default defineComponent({
  name: 'LeftSidebar',
  mixins: [util],
  data() {
    return {
      mini: null,
    };
  },
  computed: {
    isSacGroupActive() {
      const sacRoutes = ['SecurityAssuranceCase', 'SacComposerChat', 'SacTemplateCreation'];
      return sacRoutes.includes(this.$route.name);
    },
    isMoreToolsActive() {
      const moreToolsRoutes = ['PlantUml'];
      return moreToolsRoutes.includes(this.$route.name);
    },
    menuItems() {
      return [
        {
          title: this.$i18n.t('plantUml.title'),
          icon: 'mdi mdi-factory',
          to: { name: 'PlantUml' },
        },
        // {
        //   icon: 'mdi mdi-memory',
        //   title: this.$i18n.t('EU AI Act Form'),
        //   href: 'https://artificialintelligenceact.eu/assessment/eu-ai-act-compliance-checker/',
        //   target: '_blank',
        //   rel: 'noopener noreferrer',
        // },
        // {
        //   icon: 'mdi mdi-security-network',
        //   title: this.$i18n.t('Dependency Track'),
        //   href: 'http://10.10.4.3:8180',
        //   target: '_blank',
        //   rel: 'noopener noreferrer',
        // },
        // { title: this.$i18n.t('vulnerabilitiesManager.title'), icon: 'mdi-shield-lock' },
      ];
    },
    sections() {
      return [
        {
          label: null,
          items: [
            {
              icon: 'mdi-view-dashboard',
              text: this.$i18n.t('dashboard'),
              to: { name: 'Dashboard' },
            },
          ],
        },
        {
          label: 'Security',
          items: [
            {
              icon: 'mdi-package-variant-closed',
              text: this.$i18n.t('sbom.title'),
              to: { name: 'Sbom' },
            },
            {
              icon: 'mdi-spider',
              text: this.$i18n.t('Threat Modeling'),
              to: { name: 'ThreatSpiderProjects' },
            },
          ],
        },
        {
          label: 'Compliance',
          items: [
            {
              icon: 'mdi-clipboard-text-outline',
              text: this.$i18n.tc('conformityAssessment.title', 1),
              to: { name: 'ConformityAssessment' },
            },
            {
              icon: 'mdi-checkbox-marked-outline',
              text: this.$i18n.tc('soc.title', 1),
              to: { name: 'SoCList' },
            },
            {
              icon: 'mdi-shield-key-outline',
              text: 'SAC Management',
              subItems: [
                {
                  icon: 'mdi mdi-security',
                  text: this.$i18n.tc('sac.title'),
                  to: { name: 'SecurityAssuranceCase' },
                },
                {
                  icon: 'mdi-creation',
                  text: this.$i18n.t('sacComposer.title'),
                  to: { name: 'SacComposerChat' },
                },
                {
                  icon: 'mdi-file-code-outline',
                  text: 'Template Editor',
                  to: { name: 'SacTemplateCreation' },
                },
              ],
            },
            {
              icon: 'mdi-fingerprint',
              text: this.$i18n.tc('evidence.title', 1),
              to: { name: 'EvidenceList' },
            },
          ],
        },
      ];
    },
    items() {
      return this.sections.flatMap(s => s.items);
    },
  },
  methods: {
    isLoggedIn() {
      return !!localStorage.getItem('userfullname');
    },
    isNotAdminManager() {
      const roles = JSON.parse(localStorage.getItem('roles') || '[]');
      return roles[0] !== 'admin-manager';
    },
  },
});
</script>

<style scoped>
/* ========================================
   Left Sidebar - Full Height Modern Nav
   ======================================== */

/* Wrapper stretches full height of the sidebar row */
.ls-sidebar-wrapper {
  flex-shrink: 0;
  display: none;
}
@media (min-width: 768px) {
  .ls-sidebar-wrapper {
    display: block;
  }
}

/* The drawer itself: full height, gradient, flex column */
.ls-drawer {
  width: 160px;
  min-height: 100%;
  background: linear-gradient(180deg, #1a237e 0%, #1b2a8a 30%, #283593 60%, #303f9f 100%);
  box-shadow: 4px 0 24px rgba(26, 35, 126, 0.18);
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  overflow-x: hidden;
}

/* Brand Logo */
.ls-brand {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px 16px 12px;
}

.ls-brand-logo {
  width: 100px;
  height: auto;
  object-fit: contain;
  opacity: 0.95;
  transition: all 0.3s ease;
  filter: brightness(1.1);
  border-radius: 15%;
}
.ls-brand-logo:hover {
  opacity: 1;
  transform: scale(1.04);
}

/* Top Divider */
.ls-divider {
  height: 1px;
  background: rgba(255, 255, 255, 0.1);
  margin: 0 16px 8px;
}

/* Section Dividers */
.ls-section-divider {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px 4px;
}
.ls-section-line {
  flex: 1;
  height: 1px;
  background: linear-gradient(180deg, #42a5f5, #66bb6a);
  border-radius: 1px;
}
.ls-section-label {
  font-size: 0.62rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(52, 245, 4, 0.596);
  white-space: nowrap;
  flex-shrink: 0;
}

/* Navigation List */
.ls-nav-list {
  padding: 4px 10px;
  display: flex;
  flex-direction: column;
  gap: 3px;
}

/* Nav Items */
.ls-nav-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 5px;
  padding: 10px 6px;
  border-radius: 12px;
  cursor: pointer;
  text-decoration: none;
  position: relative;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Left accent bar */
.ls-nav-item::before {
  content: '';
  position: absolute;
  left: 0;
  top: 4px;
  bottom: 4px;
  width: 3px;
  background: linear-gradient(180deg, #42a5f5, #66bb6a);
  border-radius: 0 4px 4px 0;
  opacity: 0;
  transition: opacity 0.25s ease;
}

.ls-nav-item:hover {
  background: rgba(255, 255, 255, 0.08);
}
.ls-nav-item:hover::before {
  opacity: 1;
}

/* Active state */
.ls-nav-item--active {
  background: rgba(255, 255, 255, 0.14) !important;
}
.ls-nav-item--active::before {
  opacity: 1 !important;
}
.ls-nav-item--active .ls-icon-wrapper {
  background: linear-gradient(
    135deg,
    rgba(66, 165, 245, 0.45),
    rgba(102, 187, 106, 0.35)
  ) !important;
}
.ls-nav-item--active .ls-icon-wrapper .v-icon {
  color: white !important;
}
.ls-nav-item--active .ls-nav-label {
  color: white !important;
  font-weight: 700;
}

/* Icon Wrappers */
.ls-icon-wrapper {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.07);
  transition: all 0.25s ease;
}
.ls-icon-wrapper .v-icon {
  color: rgba(255, 255, 255, 0.8) !important;
  transition: color 0.2s ease;
}

.ls-nav-item:hover .ls-icon-wrapper {
  background: rgba(255, 255, 255, 0.15);
  transform: translateY(-1px);
}
.ls-nav-item:hover .ls-icon-wrapper .v-icon {
  color: white !important;
}

/* Tools icon */
.ls-icon-wrapper--tools {
  background: rgba(255, 167, 38, 0.15);
}
.ls-icon-wrapper--tools .v-icon {
  color: rgba(255, 193, 7, 0.9) !important;
}

/* Nav Label */
.ls-nav-label {
  font-size: 0.82rem;
  color: rgba(255, 255, 255, 0.7);
  text-align: center;
  word-wrap: break-word;
  line-height: 1.2;
  font-weight: 500;
  letter-spacing: 0.2px;
  max-width: 100%;
  transition: color 0.2s ease;
}
.ls-nav-item:hover .ls-nav-label {
  color: rgba(255, 255, 255, 0.95);
}

.ls-chevron-icon {
  color: rgba(255, 255, 255, 0.4) !important;
  position: absolute;
  right: 6px;
  top: 50%;
  transform: translateY(-50%);
}

/* Overflow Menu */
.ls-overflow-menu {
  border-radius: 14px !important;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.22) !important;
  background: #ffffff !important;
  padding: 0 !important;
}

.ls-overflow-header {
  background: linear-gradient(135deg, #1a237e, #283593);
  padding: 14px 18px;
  display: flex;
  align-items: center;
  color: white;
  font-weight: 600;
  font-size: 0.9rem;
  letter-spacing: 0.3px;
}

.ls-overflow-item {
  padding: 10px 18px !important;
  border-radius: 0 !important;
  min-height: 44px !important;
  transition: all 0.2s ease !important;
}
.ls-overflow-item:hover {
  background: linear-gradient(135deg, #e3f2fd, #f3f8ff) !important;
}
.ls-overflow-item:hover .v-icon {
  color: #1565c0 !important;
}

.ls-overflow-label {
  font-size: 0.9rem;
  font-weight: 500;
  color: #37474f;
}
</style>
